import FullNameField from '@/components/field/FullNameField'
import DefaultEditor from '@/components/form/DefaultEditor'
import fetchApi from '@/lib/tools/api'
import {ConsentPurpose} from '@/types'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import {Alert, Box, Chip, Grid, Stack, Typography} from '@mui/material'
import * as React from 'react'
import {useEffect} from 'react'
import {
	Button,
	Edit,
	FunctionField,
	required,
	SaveButton,
	SelectInput,
	SimpleForm,
	TextInput,
	Toolbar,
	useEditController,
	useNotify,
	useRefresh
} from 'react-admin'

export default function ConsentPurposeEdit() {

	const {record, isLoading} = useEditController<ConsentPurpose>()

	useEffect(() => {
		if (record) {
			document.title = `Édition - ${record.title}`
		}
	}, [record])

	return (

		<Edit
			title={<Title/>}
			mutationMode="pessimistic"
		>
			<SimpleForm
				record={record}
				warnWhenUnsavedChanges
				noValidate
				toolbar={<FormToolbar/>}
				sx={{
					padding: {
						md: '32px',
						xs: '16px'
					}
				}}
			>
				<Grid container display={{xs: 'block', md: 'flex'}} sx={{gap: '30px', justifyContent: 'space-between'}}>
					<Grid item xs={12}>
						<FunctionField
							source="isActive"
							label="Statut"
							render={(record: ConsentPurpose) => (
								<Box>
									<Stack direction="row" alignItems={'center'} spacing={1} sx={{mt: 1}}>
										<Typography variant="h4" gutterBottom>
											{record.title}
										</Typography>
										<Chip
											label={record.version}
											// color={record.isActive ? 'success' : 'error'}
											// icon={record.isActive ? <CheckCircleIcon/> : <CancelIcon/>}
											variant="outlined"
										/>
									</Stack>
									<Stack direction="row" alignItems={'center'} spacing={3} sx={{mt: 1}}>
										<Chip
											label={record.isActive ? 'Actif' : 'Inactif'}
											color={record.isActive ? 'success' : 'error'}
											icon={record.isActive ? <CheckCircleIcon/> : <CancelIcon/>}
											// size="small"
											// variant="outlined"
										/>
										<FunctionField
											source="versionType"
											label="Type de la dernière modification"
											render={(record: ConsentPurpose) => (
												<Box>
													<Typography variant="body2" color="textSecondary">
														Type de la dernière modification
													</Typography>
													<Typography variant="body1">
														{record.versionType} {record.versionType === 'MAJOR' ? '(Majeure)' : '(Mineure)'}
													</Typography>
												</Box>
											)}
										/>
									</Stack>
									{/*<Typography*/}
									{/*	variant="body1"*/}
									{/*	color={record.isActive ? 'success.main' : 'text.secondary'}*/}
									{/*>*/}
									{/*	{record.isActive ? 'Actif' : 'Inactif'}*/}
									{/*</Typography>*/}
								</Box>
							)}
						/>
					</Grid>
					<Grid item xs={12} md={5}>
						<Typography variant="h6" gutterBottom>
							Informations de l'usage
						</Typography>

						<TextInput
							source="code"
							label="Code"
							disabled
							fullWidth
							helperText="Le code ne peut pas être modifié après création"
						/>

						<TextInput
							source="version"
							label="Version"
							disabled
							fullWidth
							helperText="Version générée automatiquement"
						/>

						{record && !record.isActive && (
							<Alert severity="warning" sx={{mt: 2}}>
								Cet usage est inactif. Seules les versions actives peuvent être modifiées.
								Activez d'abord cet usage pour pouvoir créer une nouvelle version.
							</Alert>
						)}
					</Grid>

					<Grid item xs={12} md={6}>
						<Typography variant="h6" gutterBottom>
							Champs modifiables
						</Typography>

						<TextInput
							source="title"
							label="Titre"
							fullWidth
							validate={req}
							helperText="Titre affiché à l'utilisateur"
							disabled={!record?.isActive}
						/>

						{record?.isActive && (
							<>
								<SelectInput
									source="version_type"
									label="Type de modification"
									choices={[
										{id: 'MINOR', name: 'Mineure (x.y → x.y+1)'},
										{id: 'MAJOR', name: 'Majeure (x.y → x+1.0)'}
									]}
									defaultValue="MINOR"
									fullWidth
									helperText="Une modification majeure réinitialise le numéro mineur à 0"
									sx={{mt: 2}}
								/>
								<Alert severity="info" sx={{mt: 2, mb: 2}}>
									<Typography variant="body2">
										<strong>Important :</strong> Toute modification créera une nouvelle version du purpose.
										L'ancienne version sera désactivée automatiquement.
									</Typography>
								</Alert>
							</>
						)}

						{record?.userConsentCount !== undefined && record.userConsentCount > 0 && (
							<Alert severity="info" sx={{mt: 2}}>
								<Typography variant="body2">
									<strong>{record.userConsentCount}</strong> utilisateurs ont consenti à cet usage.
									Toute modification créera une nouvelle version et sera visible pour les nouveaux consentements.
								</Typography>
							</Alert>
						)}

						<Box sx={{mt: 2}}>
							<Typography variant="body2" color="textSecondary">
								Dates
							</Typography>
							<Typography variant="caption" display="block">
								Créé le : {record?.createdAt ? new Date(record.createdAt).toLocaleString() : '-'}
							</Typography>
							<Typography variant="caption" display="block">
								Modifié le : {record?.updatedAt ? new Date(record.updatedAt).toLocaleString() : '-'}
							</Typography>
						</Box>
					</Grid>
				</Grid>

				<Box sx={{mt: 3}}>
					<DefaultEditor
						source="description"
						label="Description détaillée"
						readOnly={!record?.isActive}
					/>
					<Typography variant="caption" color="textSecondary" sx={{mt: 1, display: 'block'}}>
						{record?.isActive
							? 'Description complète de l\'usage qui sera affichée aux utilisateurs. Toute modification créera une nouvelle version.'
							: 'Description en lecture seule (usage inactif)'
						}
					</Typography>
				</Box>
			</SimpleForm>
		</Edit>
	)
}

const req = [required()]

const Title = () => (
	<Box>
		<Typography variant="h5">
			Usage RGPD
		</Typography>
		<FullNameField source="title" sx={{fontSize: '1.2rem', fontWeight: 500}}/>
	</Box>
)

const FormToolbar = () => {
	const notify = useNotify()
	const {record} = useEditController<ConsentPurpose>()

	return (
		<Toolbar className="fixed-bottom" sx={{display: 'flex', justifyContent: 'space-between'}}>
			<Box sx={{display: 'flex', gap: 2}}>
				<SaveButton
					alwaysEnable
					className="save"
					label={record?.isActive ? "Créer nouvelle version et quitter" : "Enregistrer et quitter"}
					disabled={!record?.isActive}
				/>
				<SaveButton
					alwaysEnable
					className="save"
					label={record?.isActive ? "Créer nouvelle version" : "Enregistrer"}
					type="button"
					variant="text"
					disabled={!record?.isActive}
					mutationOptions={{
						onSuccess: (data) => {
							notify(record?.isActive 
								? 'Nouvelle version créée avec succès' 
								: 'Usage mis à jour avec succès', {
								type: 'success',
								messageArgs: {smart_count: 1}
							})
						}
					}}
				/>
			</Box>
			<ActivationButtons/>
			{/*<DeleteButton */}
			{/*    confirmTitle={`Supprimer l'usage "${record?.title}"`}*/}
			{/*    confirmContent="Cette action est irréversible. Tous les consentements associés seront supprimés."*/}
			{/*/>*/}
		</Toolbar>
	)
}

const ActivationButtons = () => {
	const {record} = useEditController<ConsentPurpose>()
	const notify = useNotify()
	const refresh = useRefresh()

	const handleActivate = async () => {
		if (!record?.id) return

		try {
			const response = await fetchApi<{ success: boolean; message?: string }>(
				`/consent/purposes/${record.id}/activate`,
				{},
				null,
				'PATCH'
			)

			notify('Usage activé avec succès', {type: 'success'})
			refresh()
		} catch (error: any) {
			const message = error?.message || 'Erreur lors de l\'activation'
			notify(message, {type: 'error'})
		}
	}

	const handleDeactivate = async () => {
		if (!record?.id) return

		// Si il y a des consentements, afficher une confirmation
		if (record.userConsentCount && record.userConsentCount > 0) {
			if (!window.confirm(
				`Attention : ${record.userConsentCount} utilisateurs ont consenti à cet usage.\n\n` +
				`La désactivation empêchera de nouveaux consentements mais conservera l'historique existant.\n\n` +
				`Voulez-vous continuer ?`
			)) {
				return
			}
		}

		try {
			const response = await fetchApi<{ success: boolean; message?: string }>(
				`/consent/purposes/${record.id}/deactivate`,
				{},
				null,
				'PATCH'
			)

			notify('Usage désactivé avec succès', {type: 'success'})
			refresh()
		} catch (error: any) {
			const message = error?.message || 'Erreur lors de la désactivation'
			notify(message, {type: 'error'})
		}
	}

	if (!record) return null

	return (
		<Box>
			{!record.isActive ? (
				<Button
					label="Activer l'usage"
					onClick={handleActivate}
					variant="contained"
				/>
			) : (
				<Button
					label="Désactiver l'usage"
					onClick={handleDeactivate}
					variant="outlined"
					color="error"
				/>
			)}
		</Box>
	)
}
